import type { z } from 'zod'

import { native } from '@/shared/native'

import { isDomainLive } from './domains'

import { ApiError, ContractError, NetworkError, toApiError } from './errors'
import { clearTokens, loadTokens, pairFrom, saveTokens, peekTokens } from './tokens'

/**
 * The one HTTP client.
 *
 * Everything the contract requires on the wire is applied here and nowhere
 * else (hard rule 2): `X-Device-Id`, `X-Client-Version`, `Accept-Language`,
 * the bearer token, `Idempotency-Key` on money-moving POSTs, rotating refresh,
 * and the shared error envelope. Feature code passes a path, a body and a Zod
 * schema; it never builds headers.
 */

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export type Query = Record<string, string | number | boolean | null | undefined>

export interface RequestOptions<TSchema extends z.ZodType | undefined> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /**
   * JSON by default. Pass a `FormData` for the multipart routes — the KYC
   * document uploads (backend-routes.md §5) — and the Content-Type header is
   * left to the browser so it can set the multipart boundary.
   */
  body?: unknown
  query?: Query
  /** Validates the response against the contract. Omit for `204`. */
  schema?: TSchema
  /** Public routes (marked 🔓) skip the bearer and never trigger a refresh. */
  auth?: boolean
  /**
   * For the endpoints where a `401` judges the credential in the body, not the
   * token in the header.
   *
   * The two-step logins authenticate with a half-finished session and send the
   * missing factor as the payload, so `/auth/login/password` answers `401`
   * `otp_invalid` for a wrong password and `401` `account_locked` for a locked
   * account. Both are verdicts on what was typed; the session is still fine and
   * is the only thing standing between the user and re-sending an SMS. Without
   * this flag the blanket clear below treats one typo as a dead session, drops
   * the pending token, and sends the user back to the phone screen for a fresh
   * code — the exact cost this flow exists to remove.
   */
  keepSessionOn401?: boolean
  /**
   * Mandatory on every money-moving POST. Pass a key you keep across retries
   * so a replay returns the original result with `200` rather than charging
   * twice — generate it with `newIdempotencyKey()` when the form opens, not
   * when it submits.
   */
  idempotencyKey?: string
  signal?: AbortSignal
}

export function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined) params.set(key, String(value))
  }
  const search = params.toString()
  return search ? `${url}?${search}` : url
}

async function baseHeaders(): Promise<Headers> {
  const headers = new Headers({
    'Accept-Language': 'fa-IR',
    'X-Device-Id': await native.device.getId(),
    'X-Client-Version': native.device.getClientVersion(),
  })
  return headers
}

/* --- refresh ---------------------------------------------------------------
 * The refresh token rotates, so two concurrent 401s must not both spend it.
 * A single in-flight promise is shared by every caller.
 *
 * **The live service mounts no refresh route.** `samin-ex-core` issues refresh
 * tokens from `/auth/login` and `/auth/otp/verify` but serves nothing that
 * spends one, so when auth is live a 401 is terminal: the tokens are cleared
 * and the user signs in again. Refreshing against the fixtures while the
 * session itself is real would mint a session the backend never issued. */
let refreshInFlight: Promise<boolean> | null = null

function refreshIsServed(): boolean {
  return !isDomainLive('auth')
}

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const tokens = await loadTokens()
      // No refresh token: the second factor promoted a session that keeps the
      // one step one issued, or the service issued none at all.
      if (!tokens?.refreshToken) return false
      const headers = await baseHeaders()
      headers.set('Content-Type', 'application/json')
      const response = await fetch(buildUrl('/auth/token/refresh'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      })
      if (!response.ok) {
        await clearTokens()
        return false
      }
      const body = (await response.json()) as {
        access_token: string
        refresh_token: string
        expires_in: number
      }
      await saveTokens(pairFrom(body.access_token, body.refresh_token, body.expires_in))
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so callers awaiting this round all see it.
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()
  return refreshInFlight
}

async function send(
  path: string,
  options: RequestOptions<z.ZodType | undefined>,
  retrying: boolean,
): Promise<Response> {
  const {
    method = 'GET',
    body,
    query,
    auth = true,
    idempotencyKey,
    signal,
    keepSessionOn401 = false,
  } = options

  const isMultipart = body instanceof FormData
  const headers = await baseHeaders()
  if (body !== undefined && !isMultipart) headers.set('Content-Type', 'application/json')
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)
  if (auth) {
    const tokens = peekTokens() ?? (await loadTokens())
    if (tokens) headers.set('Authorization', `Bearer ${tokens.accessToken}`)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : isMultipart ? body : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    if (signal?.aborted) throw cause
    throw new NetworkError(cause)
  }

  if (response.status === 401 && auth && !retrying && !keepSessionOn401) {
    if (refreshIsServed() && (await refreshSession())) return send(path, options, true)
    await clearTokens()
  }

  return response
}

export async function request<TSchema extends z.ZodType>(
  path: string,
  options: RequestOptions<TSchema> & { schema: TSchema },
): Promise<z.infer<TSchema>>
export async function request(
  path: string,
  options?: RequestOptions<undefined>,
): Promise<void>
export async function request(
  path: string,
  options: RequestOptions<z.ZodType | undefined> = {},
): Promise<unknown> {
  const response = await send(path, options, false)

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw toApiError(response.status, body)
  }

  if (!options.schema || response.status === 204) return undefined

  const payload = await response.json().catch((cause: unknown) => {
    throw new ContractError(path, cause)
  })

  const parsed = options.schema.safeParse(payload)
  if (!parsed.success) throw new ContractError(path, parsed.error.issues)
  return parsed.data
}

export const api = {
  get: <TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method' | 'body'> & { schema: TSchema },
  ) => request(path, { ...options, method: 'GET' }),

  post: <TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'> & { schema: TSchema },
  ) => request(path, { ...options, method: 'POST' }),

  patch: <TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'> & { schema: TSchema },
  ) => request(path, { ...options, method: 'PATCH' }),

  put: <TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'> & { schema: TSchema },
  ) => request(path, { ...options, method: 'PUT' }),

  /** `DELETE` answers 204 on most routes, but bank-accounts returns the row. */
  delete: (<TSchema extends z.ZodType>(
    path: string,
    options?: Omit<RequestOptions<TSchema>, 'method'> & { schema?: TSchema },
  ) => request(path, { ...options, method: 'DELETE' } as never)) as {
    (path: string, options?: Omit<RequestOptions<undefined>, 'method'>): Promise<void>
    <TSchema extends z.ZodType>(
      path: string,
      options: Omit<RequestOptions<TSchema>, 'method'> & { schema: TSchema },
    ): Promise<z.infer<TSchema>>
  },
}

export { ApiError, ContractError, NetworkError }
